import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const pct = (v) => (v == null ? '—' : `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v)}%`);

const VsCell = ({ v, testId }) => (
  <span className={`font-semibold ${v > 0 ? 'text-green-700' : v < 0 ? 'text-red-600' : 'text-gray-600'}`} data-testid={testId}>{pct(v)}</span>
);

const ExtraCell = ({ v, testId }) => (
  <span className={v > 0 ? 'text-red-600' : 'text-green-700'} data-testid={testId}>
    {v > 0 ? `${v} L extra` : v < 0 ? `${Math.abs(v)} L saved` : '0 L'}
  </span>
);

const Summary = ({ rows }) => {
  const ranked = rows.filter(r => r.vs_vehicle_pct != null);
  if (!ranked.length) return null;
  const best = ranked[0], worst = ranked[ranked.length - 1];
  return (
    <p className="text-sm text-gray-600 mb-2" data-testid="dm-summary">
      Best: <strong data-testid="dm-best">{best.name}</strong> ({pct(best.vs_vehicle_pct)} vs vehicle avg)
      {ranked.length > 1 && <> · Needs coaching: <strong data-testid="dm-worst">{worst.name}</strong> ({pct(worst.vs_vehicle_pct)}, {worst.extra_litres > 0 ? `${worst.extra_litres} L extra` : 'no extra fuel'})</>}
    </p>
  );
};

export const DriverMileageReport = ({ data }) => {
  const rows = data?.rows || [];
  const un = data?.unassigned;
  return (
    <div data-testid="dm-report">
      <Summary rows={rows} />
      <Card className="border-0 shadow-md"><CardContent className="p-0">
        <Table data-testid="dm-report-table">
          <TableHeader><TableRow>
            <TableHead>#</TableHead><TableHead>Driver</TableHead><TableHead>Vehicles</TableHead><TableHead>Km driven</TableHead>
            <TableHead>Litres</TableHead><TableHead>Km/L</TableHead><TableHead>Vehicle avg Km/L</TableHead>
            <TableHead>vs vehicle avg</TableHead><TableHead>Extra fuel</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.driver_key} data-testid={`dm-row-${r.rank}`}>
                <TableCell className="font-semibold">{r.rank}</TableCell>
                <TableCell className="font-medium" data-testid={`dm-name-${r.rank}`}>{r.name}</TableCell>
                <TableCell className="text-xs">{r.vehicles.join(', ')}</TableCell>
                <TableCell data-testid={`dm-km-${r.rank}`}>{r.km.toLocaleString('en-IN')}</TableCell>
                <TableCell>{r.litres}</TableCell>
                <TableCell className="font-semibold" data-testid={`dm-kmpl-${r.rank}`}>{r.kmpl ?? '—'}</TableCell>
                <TableCell data-testid={`dm-bench-${r.rank}`}>{r.vehicle_avg_kmpl ?? '—'}</TableCell>
                <TableCell><VsCell v={r.vs_vehicle_pct} testId={`dm-vs-${r.rank}`} /></TableCell>
                <TableCell><ExtraCell v={r.extra_litres} testId={`dm-extra-${r.rank}`} /></TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-gray-400 py-8" data-testid="dm-empty">No fill-to-fill mileage in this range.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>
      {un?.stretches > 0 && <p className="text-xs text-amber-700 mt-2" data-testid="dm-unassigned">
        {un.km.toLocaleString('en-IN')} km on {un.litres} L ({un.stretches} fill-to-fill {un.stretches === 1 ? 'stretch' : 'stretches'}) had no trips and no driver on the fill, so it isn't credited to anyone.
      </p>}
      <p className="text-[11px] text-gray-400 mt-2">The km and litres between two fills are split between the drivers who drove that vehicle in those days, by the trip km each drove. "vs vehicle avg" compares a driver with the same vehicles' own Km/L, so Tempo and Truck drivers are ranked fairly. Extra fuel = litres used beyond what those vehicles normally need for the same km.</p>
    </div>
  );
};
